import { useEffect, useState, useContext } from 'react';
import { useLocation, Link } from 'react-router-dom';
import SubmitButton from '../AuthPages/SubmitButton';
import { Context } from '../../AuthContext';

const formatDowngradedPermission = (p) => {
  // Explorer accounts cannot hold any editor role on a Joined Vertical.
  if (p === 'AdvancedEditor' || p === 'Editor' || p === 'Member') return 'Member';
  return p || '—';
};

const ReviewDowngradePage = () => {
  const { user } = useContext(Context);
  const location = useLocation();
  const {
    selectedIds = [],
    selectedVerticals = [],
    unselectedVerticals = [],
    selectedJoinedIds = [],
    selectedJoinedVerticals = [],
    unselectedJoinedVerticals = [],
    myVerticalUserConfig = {},
    usersByVertical = {},
  } = location.state || {};

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Review Downgrade - The Simple Platform';
  }, []);

  const handleConfirmDowngrade = async () => {
    setSubmitting(true);
    try {
      const token = await user.getIdToken();

      for (const vertical of unselectedVerticals) {
        const res = await fetch(
          `${process.env.REACT_APP_FLASK_WEBSERVER}delete_vertical/${vertical._id}?vertical_id=${vertical._id}&downgrade=true`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) {
          throw new Error(`Failed to delete vertical ${vertical._id}`);
        }
      }

      for (const vertical of unselectedJoinedVerticals) {
        const res = await fetch(
          `${process.env.REACT_APP_FLASK_WEBSERVER}leaveVertical?vertical_id=${vertical.id}`,
          {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) {
          throw new Error(`Failed to leave vertical ${vertical.id}`);
        }
      }

      for (const vertical of selectedJoinedVerticals) {
        if (vertical.permission !== 'AdvancedEditor' && vertical.permission !== 'Editor') continue;
        const res = await fetch(
          `${process.env.REACT_APP_FLASK_WEBSERVER}changePermission` +
            `?email=${encodeURIComponent(user.email)}` +
            `&vertical_id=${vertical.id}` +
            `&new_permission=Member`,
          {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) {
          throw new Error(`Failed to demote permission on vertical ${vertical.id}`);
        }
      }

      // For each retained My Vertical, apply the user retention config:
      // disinvite anyone unselected, set retained users' permissions explicitly.
      const keptVerticalIds = new Set(selectedIds);
      for (const [verticalId, config] of Object.entries(myVerticalUserConfig)) {
        if (!keptVerticalIds.has(verticalId)) continue;
        const verticalUsers = usersByVertical[verticalId] || [];
        const keptSet = new Set(config.keptEmails || []);
        const basicSet = new Set(config.basicEditorEmails || []);
        const calls = verticalUsers
          .map((u) => {
            if (!keptSet.has(u.email)) {
              const endpoint = u.status === 'Active' ? 'deactivateUser' : 'disinviteUser';
              return fetch(
                `${process.env.REACT_APP_FLASK_WEBSERVER}${endpoint}` +
                  `?vertical_id=${verticalId}&email=${encodeURIComponent(u.email)}`,
                { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } }
              );
            }
            const target = basicSet.has(u.email) ? 'Editor' : 'Member';
            const current = u.permission || 'Member';
            if (current === target) return null;
            return fetch(
              `${process.env.REACT_APP_FLASK_WEBSERVER}changePermission` +
                `?vertical_id=${verticalId}&email=${encodeURIComponent(u.email)}` +
                `&new_permission=${target}`,
              { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } }
            );
          })
          .filter(Boolean);
        const results = await Promise.all(calls);
        if (results.some((r) => !r.ok)) {
          throw new Error(`Failed to apply user retention for vertical ${verticalId}`);
        }
      }

      const downgradeRes = await fetch(process.env.REACT_APP_FLASK_WEBSERVER + 'select_free_plan', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!downgradeRes.ok) {
        throw new Error('Failed to apply free plan');
      }

      window.location.assign('/tryrnai');
    } catch (err) {
      console.error('Downgrade failed:', err);
      setSubmitting(false);
    }
  };

  const draftKept = selectedVerticals.filter((v) => v.invited_count === 0);
  const myKept = selectedVerticals.filter((v) => v.invited_count > 0);

  return (
    <div style={{ textAlign: 'left' }}>
      <Link
        to="/confirm-downgrade"
        state={{ selectedIds, selectedJoinedIds, myVerticalUserConfig }}
        style={{ textDecoration: 'none', color: '#356AC3' }}
      >
        &lt; Go Back
      </Link>
      <h2>Review and Confirm Downgrade</h2>
      <p style={{ fontWeight: 700, marginBottom: '4px' }}>
        You are about to apply all of your downgrade selections.
      </p>
      <p style={{ marginTop: 0, marginBottom: 0 }}>
        Unselected Private Verticals and My Verticals will be permanently deleted after downgrade. You
        may download their data on the previous page before proceeding.
      </p>
      <p style={{ marginTop: 0, marginBottom: 0 }}>
        For any Joined Verticals you leave, you will lose access, but the Vertical will remain for
        other collaborators.
      </p>
      <p style={{ marginTop: 0, marginBottom: 0 }}>
        If you were a Basic or Advanced Editor for any of those Joined Verticals, you may also
        download that Vertical’s data on the previous page.
      </p>

      <ReviewSection title="Private Verticals" columnHeader="Private Vertical Name" items={draftKept} />
      <ReviewSection title="My Verticals" columnHeader="Vertical Name" items={myKept} />
      <MyVerticalUserSummary
        keptVerticals={myKept}
        myVerticalUserConfig={myVerticalUserConfig}
        usersByVertical={usersByVertical}
      />
      <JoinedReviewSection items={selectedJoinedVerticals} />

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '32px',
        }}
      >
        <SubmitButton
          text={submitting ? 'Downgrading...' : 'Downgrade'}
          width="240px"
          onClick={handleConfirmDowngrade}
          disabled={submitting}
        />
      </div>
    </div>
  );
};

const ReviewSection = ({ title, columnHeader, items }) => (
  <div style={{ marginTop: '24px' }}>
    <h3 style={{ fontSize: '24px', marginBottom: '8px' }}>{title}</h3>
    {items.length === 0 ? (
      <p style={{ color: '#888' }}>No {title.toLowerCase()} selected to keep.</p>
    ) : (
      <div>
        <div style={headerRowStyle}>
          <span style={{ color: '#666' }}>{columnHeader}</span>
        </div>
        {items.map((vertical) => (
          <div key={vertical._id} style={itemRowStyle}>
            <span>{vertical.query}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const JoinedReviewSection = ({ items }) => (
  <div style={{ marginTop: '24px' }}>
    <h3 style={{ fontSize: '24px', marginBottom: '8px' }}>Joined Verticals</h3>
    {items.length === 0 ? (
      <p style={{ color: '#888' }}>No joined verticals selected to keep.</p>
    ) : (
      <div>
        <div style={{ ...headerRowStyle, display: 'flex' }}>
          <span style={{ color: '#666', flex: 1 }}>Joined Vertical Name</span>
          <span style={joinedReviewColStyle}>Creator</span>
          <span style={joinedReviewColStyle}>Permission</span>
        </div>
        {items.map((vertical) => (
          <div key={vertical.id} style={{ ...itemRowStyle, display: 'flex' }}>
            <span style={{ flex: 1 }}>{vertical.name}</span>
            <span style={joinedReviewColStyle}>{vertical.creator_name || '—'}</span>
            <span style={joinedReviewColStyle}>
              {formatDowngradedPermission(vertical.permission)}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const MyVerticalUserSummary = ({ keptVerticals, myVerticalUserConfig, usersByVertical }) => {
  const sections = keptVerticals
    .map((v) => {
      const config = myVerticalUserConfig[v._id] || { keptEmails: [], basicEditorEmails: [] };
      const keptSet = new Set(config.keptEmails);
      const basicSet = new Set(config.basicEditorEmails);
      const verticalUsers = usersByVertical[v._id] || [];
      const retained = verticalUsers
        .filter((u) => keptSet.has(u.email))
        .map((u) => ({
          ...u,
          finalPermission: basicSet.has(u.email) ? 'Basic Editor' : 'Member',
        }));
      return { vertical: v, retained };
    })
    .filter((s) => s.retained.length > 0);

  if (sections.length === 0) return null;

  return (
    <div style={{ marginTop: '24px' }}>
      {sections.map(({ vertical, retained }) => (
        <div key={vertical._id} style={{ marginBottom: '16px', marginLeft: '32px' }}>
          <div style={{ ...headerRowStyle, display: 'flex' }}>
            <span style={{ color: '#666', flex: 2 }}>Full Name</span>
            <span style={{ color: '#666', flex: 3 }}>Email</span>
            <span style={{ color: '#666', flex: 1 }}>Permission</span>
          </div>
          {retained.map((u) => (
            <div key={u.email} style={{ ...itemRowStyle, display: 'flex' }}>
              <span style={{ flex: 2 }}>{u.name}</span>
              <span style={{ flex: 3 }}>{u.email}</span>
              <span style={{ flex: 1 }}>{u.finalPermission}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const headerRowStyle = {
  padding: '8px 4px',
  borderBottom: '1px solid #ddd',
};

const joinedReviewColStyle = {
  width: '180px',
  color: '#444',
};

const itemRowStyle = {
  padding: '12px 4px',
  borderBottom: '1px solid #eee',
};

export default ReviewDowngradePage;
